/**
 * The documents search field (#6559): a debounced input over the browser's
 * `q`, so each keystroke neither rewrites the URL nor refetches. The page
 * mounts it in the topbar (Drive's "Search in Drive"); the picker mounts it
 * in its own top row.
 */
import React, { useEffect, useState } from "react";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";

/** How long typing must pause before the term reaches the listing. */
export const SEARCH_DEBOUNCE_MS = 250;

const SearchField: React.FC<{
  value: string;
  onChange: (q: string) => void;
  className?: string;
  /** What this field searches. The records index searches records, not
   *  documents, and says so. */
  label?: string;
  /** What the field shows while empty; the label unless a surface names
   *  itself already (the picker's row reads "Search", Claude Design round 9). */
  placeholder?: string;
  /** Reserve the clear gutter only while there is text to clear — the narrow
   *  picker's row (round 9); by default both gutters stay. */
  compactClear?: boolean;
  /** The input, for an owner that puts the cursor here (an empty listing's
   *  "Search all documents"). */
  inputRef?: React.Ref<HTMLInputElement>;
}> = ({
  value,
  onChange,
  className = "",
  label = "Search documents",
  placeholder = label,
  compactClear = false,
  inputRef,
}) => {
  const [draft, setDraft] = useState(value);
  // What the reader typed reaches the listing once typing pauses, compared
  // at that moment with the term the listing holds. It used to be committed
  // only when a debounced copy of the draft CHANGED, so a term restored from
  // outside and cleared within the window (the copy still empty from before)
  // never committed the clear, and the listing kept searching the old term
  // under an empty box (#12187).
  const commit = useDebouncedCallback((q: string) => {
    if (q !== value) onChange(q);
  }, SEARCH_DEBOUNCE_MS);
  // A term set from outside (navigation, a chip) replaces the draft and any
  // typing still waiting to land.
  useEffect(() => {
    setDraft(value);
    commit.cancel();
  }, [value, commit]);

  // The shell is the label, so a chip inside the field sits on the same
  // surface as the text and the focus ring follows the input.
  const shell = `rounded-[7px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] pl-8 ${
    compactClear && !draft ? "pr-3" : "pr-8"
  } text-[13px] shadow-[var(--fp-lift)] focus-within:border-[var(--fp-ink)]`;
  return (
    <label
      className={`relative flex h-[30px] items-center ${shell} ${className}`}
    >
      <MagnifyingGlassIcon
        size={14}
        aria-hidden
        className="pointer-events-none absolute left-3 text-[var(--fp-ink-3)]"
      />
      <input
        ref={inputRef}
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          commit(e.target.value);
        }}
        className="h-full min-w-0 flex-1 bg-transparent text-[var(--fp-ink)] outline-none placeholder:text-[var(--fp-ink-3)] [&::-webkit-search-cancel-button]:appearance-none"
      />
      {draft && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            setDraft("");
            commit.cancel();
            onChange("");
          }}
          className="absolute right-3 text-[var(--fp-ink-3)] hover:text-[var(--fp-ink)]"
        >
          <XIcon size={13} aria-hidden />
        </button>
      )}
    </label>
  );
};

export default SearchField;
